import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/hooks/use-theme';
import { AppThemeProvider } from '@/utils/theme-context';
import { RingtonePlayerProvider } from '@/contexts/RingtonePlayerContext';
import { WallpaperCategoriesProvider } from '@/contexts/WallpaperCategoriesContext';
// import { OAuthProvider } from '@/contexts/OAuthContext';
import React, { JSX, lazy, Suspense, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useExploreGuard } from '@/hooks/use-explore-guard';
import { useDisablePinchZoom } from '@/hooks/use-disable-pinch-zoom';
import AppRetiredGate from '@/components/AppRetiredGate';

// ---------------------------------------------------------------------------
// WEB APP RETIRED — set to false to bring the web app back.
// While true, App renders ONLY the AppRetiredGate notice below: no router,
// no providers, no auth hydration, no subscriptions, no back-button guards.
// ---------------------------------------------------------------------------
const APP_RETIRED = true;

// Route-level code splitting — lazy-loaded chunks are prefetched during idle time
const HomePage = lazy(() => import('@/components/HomePage'));
const UserProfilePage = lazy(() => import('@/components/UserProfilePage'));
const PrivacyPolicyPage = lazy(() => import('@/components/PrivacyPolicyPage'));
const CopyrightPage = lazy(() => import('@/components/CopyrightPage'));
const LicensePage = lazy(() => import('@/components/LicensePage'));
const SettingsPage = lazy(() => import('@/components/SettingsPage'));

/**
 * Module factories used by React.lazy above — exported so other modules
 * (e.g. HomePage, CreatorLabel) can call the same factory to warm the same
 * Vite chunk without creating a second promise chain.
 */
export const routeModules = {
  userProfile: () => import('@/components/UserProfilePage'),
  privacyPolicy: () => import('@/components/PrivacyPolicyPage'),
  copyright: () => import('@/components/CopyrightPage'),
  license: () => import('@/components/LicensePage'),
} as const;

/**
 * Fire-and-forget idle prefetch. Falls back to a 1.5 s timeout on browsers
 * that don't support requestIdleCallback (Safari < 16.4).
 */
export function prefetchRoute(importer: () => Promise<unknown>): void {
  if ('requestIdleCallback' in window) {
    (window as Window & typeof globalThis).requestIdleCallback(() => { importer(); }, { timeout: 3000 });
  } else {
    setTimeout(() => { importer(); }, 1500);
  }
}

/** Prefetch all secondary routes in one shot — safe to call multiple times. */
export function prefetchAllRoutes(): void {
  for (const importer of Object.values(routeModules)) {
    prefetchRoute(importer);
  }
}

// Prefetch secondary routes once the main bundle has hydrated
function usePrefetchRoutes() {
  useEffect(() => {
    prefetchAllRoutes();
    // Empty deps — fire once on mount, no cleanup needed (RIC is fire-and-forget)
  }, []);
}

// Minimal dark fallback — matches the app's #0a0a0f background so there's no
// white flash while a lazy chunk loads on a cold navigation.
function RouteFallback() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0a0a0f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: '2.5px solid rgba(167,139,250,0.15)',
          borderTopColor: '#a78bfa',
          animation: 'route-spin 0.7s linear infinite',
        }}
      />
      <style>{`@keyframes route-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/**
 * Thin wrapper for pages that have no overlay UI of their own (privacy policy,
 * copyright, license, etc.). The global useExploreGuard in App handles back
 * navigation for all routes, so no per-page guard is needed here.
 */
function GuardedPage({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

// Prevent browser context menu on all images (blocks "Save Image" on Android Chrome + desktop right-click)
function useImageContextMenuGuard() {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (e.target instanceof HTMLImageElement) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, []);
}

function App(): JSX.Element {
  // Single short-circuit: while retired, nothing below this line ever
  // mounts/runs (no hooks, no providers, no routes). Remove this block
  // (and flip APP_RETIRED to false) to bring the web app back.
  if (APP_RETIRED) {
    return <AppRetiredGate />;
  }

  return <AppLive />;
}

function AppLive(): JSX.Element {
  useImageContextMenuGuard();
  useDisablePinchZoom();
  usePrefetchRoutes();
  // Global Android back button guard — installed once, covers ALL routes.
  // Ensures the app never exits: non-Explore back presses navigate to "/",
  // and back on Explore is a no-op (HomePage's own handler manages that).
  useExploreGuard();

  return (
    <AppThemeProvider>
    <ThemeProvider>
      <WallpaperCategoriesProvider>
      <RingtonePlayerProvider>
        {/* NOTE: UI Generator - Uncomment OAuthProvider wrapper below if OAuth functionality is needed.
            See .claude/skills/oauth/docs/implementation-guide.md for OAuth implementation guide. */}
        {/* <OAuthProvider> */}
        <div
          id='app-container'
          className='relative min-h-screen flex flex-col bg-background'
        >
          <main id='app-main' className='flex-1'>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path='/' element={<HomePage />} />
                <Route path='/privacy-policy' element={<GuardedPage><PrivacyPolicyPage /></GuardedPage>} />
                <Route path='/copyright' element={<GuardedPage><CopyrightPage /></GuardedPage>} />
                <Route path='/license' element={<GuardedPage><LicensePage /></GuardedPage>} />
                <Route path='/settings' element={<GuardedPage><SettingsPage /></GuardedPage>} />
                <Route path='/user/:identifier' element={<UserProfilePage />} />
              </Routes>
            </Suspense>
          </main>

          <Toaster />
        </div>
        {/* </OAuthProvider> */}
      </RingtonePlayerProvider>
      </WallpaperCategoriesProvider>
    </ThemeProvider>
    </AppThemeProvider>
  );
}

export default App;
